/* eslint-disable @typescript-eslint/no-explicit-any */
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import HtmlBlock from '~/components/Helpers/HtmlBlock'
import CTABlock from '~/components/UI/Buttons/helpers/CTABlock'
import { Cards } from '~/components/Helpers/CardLoader'
import Image from '~/components/Helpers/Image'
import SearchAutocomplete from '~/components/Content/SearchAutocomplete/SearchAutocomplete'
import VideoLauncher from '~/components/Media/Core/Video/VideoLauncher/VideoLauncher'

import { useSearchAutocomplete } from '~/hooks/useAutocompleteSearch'

type StepProps = {
  content: {
    steps: Array<StepBlockProps['step']>
    showIcons: boolean
  }
  layoutColour?: AllBackgroundColours
  className?: string
}

type StepBlockProps = {
  step: {
    copy: string
    search?: {
      placeholder: string
      contentType: { id: string[] }
    }[]
    ctaButtons?: boolean
    buttons?: Array<Button['data']>
    extraContent?: Entry & {
      sys: { contentTypeId: string }
      entryThumbnail: Image
    }
    image?: Image
    showHalfStep: boolean
    halfStepCopy?: string
    halfStepLinks?: Array<Button['data']>
    backgroundColour?: VariantProps<typeof stepVariants>['backgroundColour']
  }
  showIcons: boolean
  number: number
  layoutColour?: AllBackgroundColours
  lastItem: boolean
}

const stepVariants = cva('', {
  variants: {
    backgroundColour: {
      'light-blue': 'bg-blue-l-60',
      'light-pink': 'bg-pink-60',
      'light-aqua': 'bg-green-60',
      'light-purple': 'bg-purple-60',
      'light-orange': 'bg-orange-60'
    }
  },
  defaultVariants: {
    backgroundColour: 'light-blue'
  }
})

const StepBlock = ({
  step,
  number,
  showIcons,
  layoutColour,
  lastItem
}: StepBlockProps) => {
  const {
    copy,
    search,
    ctaButtons,
    buttons,
    extraContent,
    image,
    showHalfStep,
    halfStepCopy,
    halfStepLinks,
    backgroundColour
  } = step

  const icon = image?.asset.sys.uri.replace('ink-blue', 'white') || null

  const contentType =
    extraContent?.sys.contentTypeId === 'assetImage'
      ? 'image'
      : extraContent?.sys.contentTypeId === 'assetVideo'
        ? 'video'
        : 'card'

  const previewUrl =
    isDevelopment && !image?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  // Updated search prop to match SearchItem[] type
  const updatedSearch = search?.map(item => ({
    ...item,
    contentType: Array.isArray(item.contentType)
      ? item.contentType
      : Array.isArray(item.contentType?.id)
        ? item.contentType.id.map(id => ({ id }))
        : []
  }))

  const { mappedItems, isSuccess, handleQueryChange } =
    useSearchAutocomplete(updatedSearch)

  console.log(extraContent?.entryThumbnail.asset.sys.uri)

  return (
    <div className='lg:gap-2xl sm:gap-l gap-xs-plus sm:last:-mb-2xl last:-mb-l relative flex'>
      <div className='relative flex justify-center'>
        <div className='bg-blue-m lg:h-2xl sm:h-xl mb-m sticky top-[102px] z-[1] flex h-[40px] w-[40px] items-center justify-center font-serif text-[28px] text-white sm:top-[122px] sm:w-xl sm:text-[48px] lg:w-2xl lg:text-[56px]'>
          {showIcons && icon ? (
            <img
              src={`${previewUrl}${icon}`}
              alt={image?.altText}
              role='presentation'
              className='p-2xs sm:p-2xs-plus lg:p-xs w-2xl object-cover'
            />
          ) : (
            number
          )}
        </div>
        {(!lastItem || (showHalfStep && halfStepCopy)) && (
          <svg width='2' height='100%' className='mt-m absolute top-0 z-0'>
            <rect width='2' height='100%' fill='#1d4cf2' />
          </svg>
        )}
      </div>

      <div className='sm:gap-xl gap-l sm:pb-2xl pb-l flex flex-col'>
        <div className='gap-l flex flex-col lg:flex-row lg:flex-wrap'>
          <div className='grow lg:max-w-[780px] lg:basis-1/2'>
            <HtmlBlock content={copy} className='mt-xs-plus mb-s' />

            {search && search.length > 0 && (
              <SearchAutocomplete
                placeholder={search[0].placeholder}
                data={{}}
                loading={isSuccess}
                items={mappedItems}
                onQueryChange={handleQueryChange}
                disabled={false}
                className='text-blue-d'
                type={layoutColour === 'white' ? 'Minimal' : 'Default'}
              />
            )}

            {buttons && buttons.length > 0 && (
              <div className='sm:gap-xs-plus gap-2xs pt-xs flex flex-wrap'>
                <CTABlock
                  content={buttons}
                  ctaButtons={ctaButtons}
                  backgroundColour={layoutColour}
                />
              </div>
            )}
          </div>

          {extraContent && contentType === 'card' && (
            <div className='lg:max-w-[413px] lg:basis-[calc(50%-48px)]'>
              <Cards content={extraContent} />
            </div>
          )}

          {extraContent && contentType !== 'card' && (
            <div className='aspect-3/2 shrink-0 grow lg:aspect-square lg:max-h-[413px] lg:max-w-[413px] lg:basis-[calc(50%-48px)] lg:[&>div]:aspect-square'>
              {contentType === 'image' && (
                <Image image={extraContent.entryThumbnail} />
              )}
              {contentType === 'video' && extraContent && (
                <VideoLauncher content={extraContent as any} />
              )}
            </div>
          )}
        </div>

        {showHalfStep && halfStepCopy && (
          <div className='relative flex items-center justify-end'>
            <div
              className={cn(
                stepVariants({ backgroundColour }),
                'lg:p-m sm:p-s p-xs-plus space-y-s z-[1] self-end sm:max-w-[540px] lg:max-w-[660px]'
              )}
            >
              <HtmlBlock content={halfStepCopy} />

              {halfStepLinks && (
                <div className='lg:gap-s gap-xs flex flex-wrap'>
                  {halfStepLinks && halfStepLinks.length > 0 && (
                    <CTABlock
                      content={halfStepLinks}
                      ctaButtons={false}
                      backgroundColour={backgroundColour}
                    />
                  )}
                </div>
              )}
            </div>
            <svg
              width='100%'
              height='2'
              className='absolute -left-[40px] z-0 sm:-left-[80px] lg:-left-[120px]'
            >
              <rect width='100%' height='2' className='fill-blue-m' />
            </svg>
          </div>
        )}
      </div>
    </div>
  )
}

const StepByStep = ({ content, layoutColour, className }: StepProps) => {
  const { steps, showIcons } = content

  return (
    <div className={className}>
      {steps?.map((step, index: Index) => (
        <StepBlock
          key={index}
          step={step}
          showIcons={showIcons}
          number={index + 1}
          layoutColour={layoutColour}
          lastItem={index === steps.length - 1}
        />
      ))}
    </div>
  )
}

export default StepByStep
